import {
  SignedIn,
  SignedOut,
  SignInButton,
  SignOutButton,
  SignUpButton,
} from "@clerk/clerk-react";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronsUpDown, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface ProjectSummary {
  id: string;
  name: string;
}

interface IndexLoaderData {
  projects: ProjectSummary[] | null;
}

export const Route = createFileRoute("/")({
  loader: async (): Promise<IndexLoaderData> => {
    const res = await fetch("/api/projects", { credentials: "include" });
    if (res.status === 401) {
      return { projects: null };
    }
    if (!res.ok) {
      throw new Error("Failed to load projects");
    }
    const data = (await res.json()) as { projects: ProjectSummary[] };
    return { projects: data.projects };
  },
  component: Index,
});

function Index() {
  const { projects } = Route.useLoaderData();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-20">
      <div className="flex w-full max-w-3xl flex-col items-center gap-10 text-center">
        <span className="text-7xl" role="img" aria-label="Bear">
          🐻
        </span>
        <SignedIn>
          <SignedInLanding initialProjects={projects} />
        </SignedIn>
        <SignedOut>
          <SignedOutLanding />
        </SignedOut>
      </div>
    </div>
  );
}

interface SignedInLandingProps {
  initialProjects: ProjectSummary[] | null;
}

function SignedInLanding({ initialProjects }: SignedInLandingProps) {
  const navigate = Route.useNavigate();
  const [projects, setProjects] = useState<ProjectSummary[]>(
    initialProjects ?? [],
  );
  const [isLoadingProjects, setIsLoadingProjects] = useState(
    initialProjects === null,
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    if (initialProjects !== null) {
      setProjects(initialProjects);
      setIsLoadingProjects(false);
      setLoadError(null);
      return;
    }

    let cancelled = false;
    setIsLoadingProjects(true);
    setLoadError(null);

    (async () => {
      try {
        const res = await fetch("/api/projects", { credentials: "include" });
        if (!res.ok) {
          throw new Error(`Failed to fetch projects (${res.status})`);
        }
        const data = (await res.json()) as { projects: ProjectSummary[] };
        if (cancelled) {
          return;
        }
        setProjects(data.projects);
      } catch (error) {
        if (cancelled) {
          return;
        }
        setLoadError("We couldn't load your projects. Please try again.");
      } finally {
        if (!cancelled) {
          setIsLoadingProjects(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [initialProjects]);

  const selectedProject = selectedProjectId
    ? (projects.find((project) => project.id === selectedProjectId) ?? null)
    : null;

  const filterProjects = useCallback(
    (_value: string, search: string, keywords?: string[]) => {
      const normalizedSearch = search.trim().toLowerCase();
      if (!normalizedSearch) {
        return 1;
      }

      return keywords?.some((keyword) =>
        keyword.toLowerCase().includes(normalizedSearch),
      )
        ? 1
        : 0;
    },
    [],
  );

  const handleProjectSelect = (projectId: string) => {
    const project = projects.find((item) => item.id === projectId);
    if (!project) {
      return;
    }
    setSelectedProjectId(project.id);
    setIsPickerOpen(false);
    void navigate({
      to: "/p/$projectId",
      params: { projectId: project.id },
    });
  };

  const handleCreateProject = async () => {
    setCreateError(null);
    setIsCreatingProject(true);
    try {
      const res = await fetch("/api/projects", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        throw new Error(`Failed to create project (${res.status})`);
      }
      const data = (await res.json()) as { id: string };
      void navigate({
        to: "/p/$projectId",
        params: { projectId: data.id },
      });
    } catch (error) {
      setCreateError("We couldn't create a new project. Please try again.");
    } finally {
      setIsCreatingProject(false);
    }
  };

  return (
    <div className="flex w-full max-w-xl flex-col items-center gap-8">
      <div className="space-y-3">
        <h1 className="text-4xl font-semibold tracking-tight">
          Welcome to Bear
        </h1>
        <p className="text-muted-foreground">
          Choose a project to keep building or spin up a brand new one.
        </p>
      </div>
      <div className="flex w-full max-w-md flex-col gap-4 text-left">
        <label className="text-sm font-medium text-foreground/80">
          Your projects
        </label>
        <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={isPickerOpen}
              className="w-full justify-between"
              disabled={isLoadingProjects}
            >
              {isLoadingProjects ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading projects…
                </span>
              ) : selectedProject ? (
                selectedProject.name
              ) : projects.length > 0 ? (
                "Select a project"
              ) : (
                "No projects yet"
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <Command filter={filterProjects}>
              <CommandInput placeholder="Search projects…" />
              <CommandList>
                <CommandEmpty>No projects found.</CommandEmpty>
                <CommandGroup>
                  {projects.map((project) => (
                    <CommandItem
                      key={project.id}
                      value={project.id}
                      keywords={[project.name]}
                      onSelect={() => handleProjectSelect(project.id)}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{project.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {project.id}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {loadError ? (
          <p className="text-sm text-destructive">{loadError}</p>
        ) : null}
        {!isLoadingProjects && projects.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            You don&apos;t have any projects yet. Create one to get started.
          </p>
        ) : null}
        <Button
          size="lg"
          className="w-full"
          onClick={handleCreateProject}
          disabled={isCreatingProject}
        >
          {isCreatingProject ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Creating project…
            </span>
          ) : (
            "New project"
          )}
        </Button>
        {createError ? (
          <p className="text-sm text-destructive">{createError}</p>
        ) : null}
        <SignOutButton redirectUrl="/">
          <Button variant="outline" size="lg" className="w-full">
            Sign out
          </Button>
        </SignOutButton>
      </div>
    </div>
  );
}

function SignedOutLanding() {
  return (
    <div className="flex w-full max-w-xl flex-col items-center gap-8">
      <div className="space-y-3">
        <h1 className="text-4xl font-semibold tracking-tight">
          Welcome to Bear
        </h1>
        <p className="text-muted-foreground">
          Sign in to pick up where you left off or create a new account to get
          started.
        </p>
      </div>
      <div className="flex w-full flex-col items-center gap-3 sm:flex-row sm:justify-center">
        <SignInButton mode="modal">
          <Button size="lg" className="min-w-[160px]">
            Sign in
          </Button>
        </SignInButton>
        <SignUpButton mode="modal">
          <Button variant="outline" size="lg" className="min-w-[160px]">
            Sign up
          </Button>
        </SignUpButton>
      </div>
    </div>
  );
}
